import * as React from 'react';
import {
    ContextualMenu,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    PrimaryButton,
    TextField,
} from '@fluentui/react';
import { Attachment, updateAttachment, SmRefType as SmRefTypeEnum } from 'api/attachment';
import { FormTextField } from 'formComponents/FormTextField';
import { SavingState } from 'api/savingState';
import { isEqual } from 'lodash';
import { logDifference } from 'utils/helper';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { useIsMounted } from 'hooks/useIsMounted';
import { formStyles } from 'styles/formStyles';
import { AttachmentArt } from 'formComponents/lookups/AttachmentArt';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { SmRefType } from 'formComponents/lookups/SmRefType';
import { SmRefTypeLabel } from 'utils/labels';
import { FormDatePicker } from 'formComponents/FormDatePicker';

const styles = mergeStyleSets({
    data: {
        marginTop: 15,
        display: 'grid',
        gridTemplateColumns: '150px 1fr 170px 120px 100px max-content',
        gridColumnGap: 10,
        minHeight: 200,
    },
});

interface EditAttachmentProps {
    attachment: Attachment;
    onClose: (refetch: boolean) => void;
}

export const EditAttachment: React.FC<EditAttachmentProps> = ({ attachment, onClose }) => {
    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [attachmentData, setAttachmentData] = React.useState(attachment);
    const isMounted = useIsMounted();

    React.useEffect(() => {
        if (savingState === SavingState.notChanged && !isEqual(attachmentData, attachment)) {
            setSavingState(SavingState.notSaved);
            logDifference('attachment', attachmentData, attachment);
        }
    }, [savingState, attachmentData, attachment]);

    const save = React.useCallback(() => {
        if (!attachmentData) {
            return;
        }

        if (savingState !== SavingState.saving) {
            setSavingState(SavingState.saving);
            updateAttachment(attachmentData)
                .then(() => {
                    setSavingState(SavingState.saved);
                    if (isMounted) {
                        setSavingState(SavingState.notChanged);
                    }

                    onClose(true);
                })
                .catch((error) => {
                    console.error(error);
                    setSavingState(SavingState.error);
                });
        }
    }, [attachmentData, isMounted, onClose, savingState]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title="Dokument bearbeiten"
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    layerProps: { eventBubblingEnabled: true },
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth={1000}
                onDismiss={() => onClose(false)}
            >
                <form className={formStyles.form}>
                    <div className={styles.data}>
                        <FormDatePicker
                            label="Datum"
                            savingState={savingState}
                            value={attachmentData.datum}
                            onChange={(newValue) => {
                                setAttachmentData({
                                    ...attachmentData,
                                    datum: newValue,
                                });
                            }}
                            options={{
                                required: true,
                            }}
                        />

                        <FormTextField
                            value={attachmentData.text}
                            savingState={savingState}
                            options={{}}
                            onChange={(newValue): void => {
                                setAttachmentData({
                                    ...attachmentData,
                                    text: newValue as string,
                                });
                            }}
                            label={'Beschreibung - ' + attachmentData.file}
                        />
                        <div>
                            <AttachmentArt
                                value={attachmentData.art.toString()}
                                disabled={savingState === SavingState.saving}
                                menuPlacement="bottom"
                                onChange={(newValue): void => {
                                    setAttachmentData({
                                        ...attachmentData,
                                        art: parseInt(newValue, 10),
                                    });
                                }}
                            />
                        </div>
                        <div>
                            <SmRefType
                                label="Verbunden mit..."
                                value={attachmentData.smRefType || ''}
                                disabled={savingState === SavingState.saving}
                                menuPlacement="bottom"
                                allowEmpty
                                onChange={(newValue): void => {
                                    setAttachmentData({
                                        ...attachmentData,
                                        smRefType: newValue as SmRefTypeEnum,
                                    });
                                }}
                            />
                        </div>
                        <TextField
                            label={(SmRefTypeLabel[attachmentData.smRefType || ''] || '?') + '-Nr.'}
                            value={attachmentData.smRef}
                            disabled={savingState === SavingState.saving}
                            onChange={(event, newValue) => {
                                setAttachmentData({
                                    ...attachmentData,
                                    smRef: newValue ?? '',
                                });
                            }}
                        />
                        <div>
                            <Checkbox
                                className={formStyles.checkboxInsideFormAligned}
                                label="Für AD sichtbar"
                                disabled={savingState === SavingState.saving}
                                checked={attachmentData.anzeige}
                                onChange={(event, checked) => {
                                    setAttachmentData({
                                        ...attachmentData,
                                        anzeige: !!checked,
                                    });
                                }}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <DefaultButton
                            text="In OneDrive öffnen"
                            disabled={savingState !== SavingState.notChanged || !attachment.oneDriveUrl}
                            onClick={() => {
                                if (attachment.oneDriveUrl) {
                                    window.open(attachment.oneDriveUrl);
                                }
                            }}
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged}
                            />
                        )}
                    </DialogFooter>
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}
                </form>
            </Dialog>
        </>
    );
};
